import classNames from "classnames"
import isEmpty from "lodash.isempty"
import debounce from "lodash.debounce"
import { useCallback } from "react"

import { Icon } from "web/components"
import useDropDownMenu from "web/hooks/behaviour/useDropDownMenu"
import { formatValue } from "web/services/format"

import EditConditionDropDown from "./DropDowns"
import Operator from "./Operator"

import { getOperators } from "./DropDowns/helpers"
import { humanize } from "web/services/inflector"

function getDisplayText(value, operator) {
  const isList = Array.isArray(value)

  if (["empty", "not_empty"].includes(operator)) {
    return `is ${humanize(operator).toLowerCase()}`
  }

  if (isEmpty(value)) {
    return "All"
  }

  // Special case for user reference fields
  if (value?.value === "current") {
    return "You"
  }

  // Special cases for dates
  if (operator === "relative_interval") {
    if (value.value.includes("ago")) {
      return `last ${value.value.replace(" ago", "")}`
    } else {
      return `next ${value.value}`
    }
  }

  if (value?.value === "now") {
    return "Today"
  }

  if (!isList) {
    return formatValue(value)
  }

  if (value.length === 1) {
    return formatValue(value[0])
  }

  if (operator === "range") {
    return `${formatValue(value[0])} - ${formatValue(value[1])}`
  }

  return `${value.length} Selected`
}

function CloseButton({ onClick }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClose = useCallback(
    debounce(onClick, Infinity, {
      leading: true,
    }),
    []
  )

  return <Icon name="x-mark" onClick={handleClose} width="15px" height="15px" />
}

export default function ConditionPill({
  field,
  condition,
  onAdd,
  onChange,
  onDelete,
}) {
  const id = `filter-condition-${condition.id}`
  const {
    DropDownMenu,
    menuVisible: dropDownOpen,
    toggleMenu: toggleDropDown,
  } = useDropDownMenu({
    triggerElementId: id,
  })

  const isUsed =
    !isEmpty(condition.value) || condition.empty_or_not_empty_operator

  const isShowOperator =
    !isEmpty(condition.value) && !condition.empty_or_not_empty_operator

  const classes = classNames(
    "flex text-sm border items-center rounded-full ml-2 mt-2 py-2 px-2 w-full md:w-auto shadow-sm hover:bg-primary-10 hover:border-blue-300 cursor-pointer",
    {
      "bg-primary-10 border-blue-100 text-primary-1": isUsed,
      "bg-white border-neutral-9 text-neutral-2": !isUsed,
    }
  )

  async function handleChange(updatedConditionValue) {
    const isEmptyOrNotEmptyOperator = ["empty", "not_empty"].includes(
      updatedConditionValue.operator
    )

    if (isEmptyOrNotEmptyOperator) {
      updatedConditionValue.value = null
      updatedConditionValue.match_blank = null
    } else {
      if (isEmpty(updatedConditionValue.value)) {
        return handleClear()
      }
    }

    const fromBackend = condition.id !== condition.field

    if (!fromBackend) {
      await onAdd(updatedConditionValue)
    } else {
      await onChange(condition, updatedConditionValue)
    }
    toggleDropDown()
  }

  function getFirstOperator() {
    return getOperators(field.kind)[0].value
  }

  function handleClear(defaultCondition = condition) {
    toggleDropDown()

    if (condition.empty_or_not_empty_operator) {
      defaultCondition.operator = getFirstOperator()
    }

    onChange(condition, { ...defaultCondition, value: {} })
  }

  function handleDelete() {
    onDelete(condition)
  }

  return (
    <>
      <div
        key={id}
        id={id}
        className={classes}
        data-testid={`filter-condition-${field.name}`}
      >
        <CloseButton onClick={handleDelete} />
        <button
          onClick={toggleDropDown}
          data-testid={`button-filter-condition-${field.name}`}
          className="flex flex-grow pl-2 focus:outline-none"
        >
          <span className="flex-grow pr-4 font-bold">{field.label}</span>
          <span className="pr-2">
            {isShowOperator && (
              <Operator operator={condition.operator} disableEquals={true} />
            )}
            <span className="ml-1">
              {getDisplayText(condition.value, condition.operator)}
            </span>
          </span>
          <Icon name="chevron-down" />
        </button>
      </div>
      {dropDownOpen && (
        <DropDownMenu>
          <EditConditionDropDown
            field={field}
            condition={condition}
            onCancel={toggleDropDown}
            onChange={handleChange}
            onClear={handleClear}
          />
        </DropDownMenu>
      )}
    </>
  )
}
